import { generatePath } from 'react-router-dom';
import { BaseQueryParams } from 'common/models';
import { Paginated } from 'common/utils';
import apiPaths from 'config/apiPaths';
import { ApplicationUser } from 'modules/ApplicationUsers/models/application-user';

import { buildBasicPaginatedSearchParams } from '../common/utils/searchParams.helper';
import { DebtTableStatus } from '../modules/ApplicationUsers/EcoFee/model/enums/debt-status.enum';
import { PaymentHistoryStatus } from '../modules/ApplicationUsers/EcoFee/model/enums/payment-history-status.enum';
import { PaymentHistoryResponse } from '../modules/ApplicationUsers/EcoFee/model/payment-history.response';
import { UserDebtItemResponse } from '../modules/ApplicationUsers/EcoFee/model/user-debt-item.response';
import { ApplicationUserProfileSettingsRequest } from '../modules/ApplicationUsers/models/application-user-notification-settings.request.model';
import { ApplicationUserProfileSettingsResponse } from '../modules/ApplicationUsers/models/application-user-notification-settings.response.model';
import axiosInstance from './axios';

export const getApplicationUserList = ({
  searchText,
  page,
  limit,
}: BaseQueryParams): Promise<Paginated<ApplicationUser>> => {
  const searchParams = buildBasicPaginatedSearchParams(page, limit, searchText);
  return axiosInstance.get<Paginated<ApplicationUser>>(`${apiPaths.USERS}?${searchParams}`).then(({ data }) => data);
};

export const anonymizeApplicationUser = (id: string): Promise<void> => axiosInstance.delete(`${apiPaths.USERS}/${id}`);

export const getUserProfileSettings = (userId: string): Promise<ApplicationUserProfileSettingsResponse> =>
  axiosInstance
    .get<ApplicationUserProfileSettingsResponse>(generatePath(apiPaths.USERS_SETTINGS, { userId }))
    .then(({ data }) => data);

export const updateUserProfileSettings = (
  userId: string,
  settingsRequest: ApplicationUserProfileSettingsRequest,
): Promise<ApplicationUserProfileSettingsResponse> =>
  axiosInstance.put(generatePath(apiPaths.USERS_SETTINGS, { userId }), { ...settingsRequest });

export const getUserDebts = (
  userId: string,
  { page, limit, searchText, status }: BaseQueryParams & { status?: DebtTableStatus },
): Promise<Paginated<UserDebtItemResponse>> => {
  const additionalParams = new Map<string, string>();
  if (status) {
    additionalParams.set('status', status);
  }
  const searchParams = buildBasicPaginatedSearchParams(page, limit, searchText, additionalParams);
  return axiosInstance
    .get<Paginated<UserDebtItemResponse>>(`${generatePath(apiPaths.USERS_DEBTS, { userId })}?${searchParams}`)
    .then(({ data }) => data);
};

export const cancelUserDebt = (userId: string, debtId: string): Promise<void> =>
  axiosInstance.delete(generatePath(apiPaths.USERS_DEBTS_DEBT, { userId, debtId }));

export const getUserPayments = (
  userId: string,
  { page, limit, searchText, status }: BaseQueryParams & { status?: PaymentHistoryStatus },
): Promise<Paginated<PaymentHistoryResponse>> => {
  const additionalParams = new Map<string, string>();
  if (status) {
    additionalParams.set('status', status);
  }
  const searchParams = buildBasicPaginatedSearchParams(page, limit, searchText, additionalParams);
  return axiosInstance
    .get<Paginated<PaymentHistoryResponse>>(`${generatePath(apiPaths.USERS_PAYMENTS, { userId })}?${searchParams}`)
    .then(({ data }) => data);
};
